import { inject, Injectable, signal } from '@angular/core'
import { Store } from '@ngrx/store'
import { map, Observable, switchMap, tap } from 'rxjs'
import {
  appendMessage,
  createConversation,
  setConversationTitle,
} from '../../../actions/analytics-copilot.actions'
import * as fromRoot from '../../../reducers'
import { PusherService } from '../../../services/pusher.service'
import {
  CopilotActivityEntry,
  CopilotConversation,
  CopilotConversationDetail,
  CopilotConversationListItem,
  CopilotMessage,
  CopilotQueryStatus,
  CopilotResultResponse,
  CopilotToolActivity,
} from '../models/copilot.model'
import { CopilotService } from './copilot.service'

export interface CopilotRealtimeState {
  isThinking: boolean
  thinkingStep: number
  thinkingLabel: string | null
  errorMessage: string | null
  currentActivity: CopilotActivityEntry[]
}

export const EMPTY_REALTIME_STATE: CopilotRealtimeState = {
  isThinking: false,
  thinkingStep: 0,
  thinkingLabel: null,
  errorMessage: null,
  currentActivity: [],
}

interface PendingMeta {
  conversationId: string
  userId: string
}

@Injectable({ providedIn: 'root' })
export class CopilotRealtimeService {
  readonly #store = inject(Store<fromRoot.State>)
  readonly #pusherService = inject(PusherService)
  readonly #copilotService = inject(CopilotService)

  private readonly _states = signal<Record<string, CopilotRealtimeState>>({})
  readonly states = this._states.asReadonly()

  private readonly pendingMessages = new Map<string, PendingMeta>()

  /**
   * Single source of truth for Pusher event handlers. `bindEvents`/`unbindEvents`
   * iterate these keys, so adding an event here wires up both bind and unbind.
   * Each handler runs only when `metaFor` resolves the pending message.
   */
  private readonly handlers: Record<
    string,
    (payload: Record<string, unknown>, meta: PendingMeta) => void
  > = {
    copilot_progress: (payload, meta) => {
      this.patch(meta.conversationId, {
        thinkingStep: typeof payload['step'] === 'number' ? payload['step'] : 0,
        thinkingLabel:
          typeof payload['label'] === 'string' ? payload['label'] : null,
      })
    },

    copilot_thinking: (payload, meta) => {
      this.patch(meta.conversationId, {
        currentActivity: [
          ...this.stateOf(meta.conversationId).currentActivity,
          { kind: 'thinking', content: String(payload['content'] ?? '') },
        ],
      })
    },

    copilot_tool_use: (payload, meta) => {
      this.patch(meta.conversationId, {
        currentActivity: [
          ...this.stateOf(meta.conversationId).currentActivity,
          {
            kind: 'tool',
            id: String(payload['id'] ?? ''),
            name: String(payload['name'] ?? ''),
            input: payload['input'],
            result: null,
          },
        ],
      })
    },

    copilot_tool_result: (payload, meta) => {
      const toolId = String(payload['id'] ?? '')
      this.patch(meta.conversationId, {
        currentActivity: this.stateOf(meta.conversationId).currentActivity.map(
          entry =>
            entry.kind === 'tool' && entry.id === toolId
              ? ({
                  ...entry,
                  result: String(payload['result'] ?? ''),
                } as CopilotToolActivity)
              : entry
        ),
      })
    },

    copilot_learnings: (payload, meta) => {
      const items = Array.isArray(payload['items'])
        ? (payload['items'] as string[])
        : []
      this.patch(meta.conversationId, {
        currentActivity: [
          ...this.stateOf(meta.conversationId).currentActivity,
          { kind: 'learnings', items },
        ],
      })
    },

    copilot_summary: (payload, meta) => {
      this.#store.dispatch(
        setConversationTitle({
          userId: meta.userId,
          conversationId: meta.conversationId,
          title: String(payload['title'] ?? ''),
        })
      )
    },

    copilot_completed: (payload, meta) => {
      const messageId = String(payload['message_id'] ?? '')
      this.#copilotService
        .getMessageResult(meta.conversationId, messageId)
        .subscribe({
          next: result => {
            // Guard: rehydratePending may have already appended this while the
            // HTTP call was in flight. Without it the message double-appends.
            if (!this.pendingMessages.has(messageId)) return
            this.appendAssistantMessage(
              meta.userId,
              meta.conversationId,
              result
            )
            this.patch(meta.conversationId, { isThinking: false })
            this.pendingMessages.delete(messageId)
          },
          error: () => {
            this.patch(meta.conversationId, {
              isThinking: false,
              errorMessage:
                'Something went wrong while preparing your report. Please try again.',
            })
            this.pendingMessages.delete(messageId)
          },
        })
    },

    copilot_failed: (payload, meta) => {
      const messageId = String(payload['message_id'] ?? '')
      this.patch(meta.conversationId, {
        isThinking: false,
        thinkingLabel: null,
        currentActivity: [],
        errorMessage:
          'Something went wrong while preparing your report. Please try again.',
      })
      this.pendingMessages.delete(messageId)
    },
  }

  bindEvents(): void {
    const ch = this.#pusherService.userChannel
    if (!ch) return
    for (const [event, handler] of Object.entries(this.handlers)) {
      ch.bind(event, (data: { message: Record<string, unknown> }) => {
        const meta = this.metaFor(data.message)
        if (meta) handler(data.message, meta)
      })
    }
  }

  unbindEvents(): void {
    const ch = this.#pusherService.userChannel
    if (!ch) return
    for (const event of Object.keys(this.handlers)) ch.unbind(event)
  }

  /**
   * Create a new conversation, submit its first question, and register the
   * resulting message as pending. Used by the landing component, which only
   * needs the created conversation back so it can navigate to it.
   */
  startConversation(params: {
    userId: string
    question: string
  }): Observable<CopilotConversationListItem> {
    const { userId, question } = params
    const trimmed = question.trim()

    return this.#copilotService.createConversation().pipe(
      switchMap(conv =>
        this.#copilotService
          .createUserQuery(conv.id, trimmed)
          .pipe(map(({ message_id }) => ({ conv, message_id })))
      ),
      tap(({ conv, message_id }) => {
        const newConv: CopilotConversation = {
          id: conv.id,
          title: conv.title ?? null,
          messages: [],
          isPinned: conv.is_pinned,
          createdAt: new Date(conv.created_at).getTime(),
          updatedAt: new Date(conv.updated_at).getTime(),
        }
        this.#store.dispatch(
          createConversation({ userId, conversation: newConv })
        )
        this.#store.dispatch(
          appendMessage({
            userId,
            conversationId: conv.id,
            message: { role: 'user', content: trimmed },
          })
        )
        this.registerPending(message_id, { conversationId: conv.id, userId })
      }),
      map(({ conv }) => conv)
    )
  }

  /** Submit a follow-up question to an existing conversation. */
  submit(params: {
    userId: string
    conversationId: string
    question: string
  }): void {
    const { userId, conversationId, question } = params

    const trimmed = question.trim()
    if (!trimmed) return
    if (this.stateOf(conversationId).isThinking) return

    this.#store.dispatch(
      appendMessage({
        userId,
        conversationId,
        message: { role: 'user', content: trimmed },
      })
    )

    this.patch(conversationId, {
      isThinking: true,
      thinkingStep: 0,
      thinkingLabel: null,
      errorMessage: null,
      currentActivity: [],
    })

    this.#copilotService.createUserQuery(conversationId, trimmed).subscribe({
      next: ({ message_id }) => {
        this.pendingMessages.set(message_id, { conversationId, userId })
      },
      error: () => {
        this.patch(conversationId, {
          isThinking: false,
          errorMessage:
            'Something went wrong while preparing your report. Please try again.',
        })
      },
    })
  }

  /**
   * Register a pending message for a new conversation created by the landing component.
   * Called after the landing component POSTs to get the conversation_id + message_id.
   */
  registerPending(messageId: string, meta: PendingMeta): void {
    this.pendingMessages.set(messageId, meta)
    this.patch(meta.conversationId, {
      isThinking: true,
      thinkingStep: 0,
      thinkingLabel: null,
      errorMessage: null,
      currentActivity: [],
    })
  }

  rehydratePending(params: {
    userId: string
    conversation: CopilotConversationDetail
  }): void {
    const { userId, conversation } = params
    const processing = conversation.messages.filter(
      m => m.status === CopilotQueryStatus.Processing
    )

    for (const m of processing) {
      this.patch(conversation.id, {
        isThinking: true,
        thinkingStep: 0,
        thinkingLabel: null,
        errorMessage: null,
        currentActivity: [],
      })

      this.pendingMessages.set(m.id, {
        conversationId: conversation.id,
        userId,
      })

      this.#copilotService.getMessageResult(conversation.id, m.id).subscribe({
        next: result => {
          // Guard: Pusher may have already handled this while the HTTP call was in flight
          if (!this.pendingMessages.has(m.id)) return
          this.appendAssistantMessage(userId, conversation.id, result)
          this.patch(conversation.id, { isThinking: false })
          this.pendingMessages.delete(m.id)
        },
        // On error the message is still processing — #pendingMessages is already set,
        // so the Pusher copilot_completed event will handle completion.
      })
    }
  }

  /**
   * Drop all realtime state and pending messages for a conversation. Keeps the
   * states map in lockstep with the NgRx conversation list — call when a
   * conversation is removed so neither map grows unbounded over a session.
   */
  clear(conversationId: string): void {
    this._states.update(states => {
      if (!(conversationId in states)) return states
      const { [conversationId]: _removed, ...rest } = states
      return rest
    })

    for (const [messageId, meta] of this.pendingMessages) {
      if (meta.conversationId === conversationId) {
        this.pendingMessages.delete(messageId)
      }
    }
  }

  private metaFor(payload: Record<string, unknown>): PendingMeta | undefined {
    const messageId = String(payload['message_id'] ?? '')
    return this.pendingMessages.get(messageId)
  }

  private stateOf(conversationId: string): CopilotRealtimeState {
    return this._states()[conversationId] ?? EMPTY_REALTIME_STATE
  }

  private patch(
    conversationId: string,
    patch: Partial<CopilotRealtimeState>
  ): void {
    this._states.update(states => {
      const current = states[conversationId] ?? EMPTY_REALTIME_STATE
      return { ...states, [conversationId]: { ...current, ...patch } }
    })
  }

  private appendAssistantMessage(
    userId: string,
    conversationId: string,
    apiResult: CopilotResultResponse
  ): void {
    const activity = this.stateOf(conversationId).currentActivity
    const result = {
      type: apiResult.result_type,
      content: apiResult.content,
    } as CopilotMessage['result']

    const message: CopilotMessage = {
      role: 'assistant',
      content:
        apiResult.result_type === 'html'
          ? '[Generated report]'
          : apiResult.content,
      result,
    }
    if (activity.length > 0) {
      message.activity = [...activity]
    }

    this.#store.dispatch(appendMessage({ userId, conversationId, message }))
    this.patch(conversationId, {
      currentActivity: [],
      thinkingLabel: null,
    })
  }
}
